/**
 * **What the Inspect tool says about one cell** (`combatApp/useInspectTool.ts`), as pure data;
 * `InspectReadout.tsx` draws it. Developer, 2026-09-28: "an 'inspector' tool to the toolbar that
 * shows information about the hovered tile."
 *
 * It authors no rules wording. The tile's name and property sentences, and the walls, objects and
 * clouds on the cell, are {@link describeCell}'s (the Map key's hovered-tile pane, pinned by the
 * rules-copy registry). The mechanics groups are the overlay's own labels (`terrainMechanics.ts`),
 * and the zone names the board's (`sitrepZoneLabel.ts`).
 *
 * **Disclosure is the client's.** It reads the state this client holds, which the server already
 * projected for this viewer (`projectEncounterFor` drops every unit the viewer may not see), and a
 * unit's HP from `tokenChromeFor`, the same projection the token's HP bar reads. So it names only
 * what the board draws for this viewer.
 */
import { roomContainsCell, worldFor, type EncounterState, type SquareCell } from "@lancer-vtt/domain";
import { clearancePercent } from "@lancer-vtt/rules";
import type { TokenChrome } from "../../lib/encounter/tokenChrome";
import type { SitrepZoneRender } from "./pixiCombatBoard";
import { ZONE_KIND_LABEL } from "./sitrepZoneLabel";
import { describeCell, type CarrierNote } from "./terrainCellDescription";
import { piecesContaining, terrainMechanicsPieces, type TerrainMechanicsPiece } from "./terrainMechanics";

/** The heading of a cell with nothing painted on it. */
export const OPEN_GROUND = "Open ground";

/** A unit or map asset standing on the cell. */
export type InspectUnit = {
  id: string;
  name: string;
  /** `HP 12/20`, or `null` when this viewer isn't shown the unit's HP. */
  hp: string | null;
  /** What an object or asset does to movement, sight and cover (`describeCell`'s object note); `null` for a unit. */
  detail: string | null;
};

export type InspectReadoutData = {
  /** The cell's `x,y` key. */
  key: string;
  /** The tile's name, "Custom terrain", or {@link OPEN_GROUND}. */
  name: string;
  /** One plain-words sentence per tile property. */
  lines: string[];
  units: InspectUnit[];
  /** Walls on the cell's edges and clouds over it. */
  carriers: CarrierNote[];
  /** The sitrep zones and rooms the cell is in. */
  zones: string[];
  /** The label of every terrain mechanics group containing the cell: the groups the board outlines. */
  regions: string[];
};

export function inspectCell(args: {
  state: EncounterState;
  cell: SquareCell;
  board: { width: number; height: number };
  /** `tokenChromeFor`'s projection for this viewer; a unit missing from it shows no HP. */
  chrome?: Readonly<Record<string, TokenChrome>>;
  /** The board's sitrep zones with their clearance (`useSitrepZones`). */
  zones?: readonly SitrepZoneRender[];
  /** The overlay's pieces for this terrain, when the caller already has them. */
  pieces?: readonly TerrainMechanicsPiece[];
}): InspectReadoutData {
  const { state, cell, board, chrome = {}, zones = [] } = args;
  const described = describeCell({ state, cell });
  const objectNotes = new Map(described.carriers.filter((n) => n.carrier === "object").map((n) => [n.id, n.detail]));
  const space = worldFor(state);

  const units: InspectUnit[] = [];
  for (const c of Object.values(state.combatants)) {
    if (c.pos == null || !space.footprint(c.pos, c.base.size).contains(cell)) continue;
    const vitals = chrome[c.id];
    units.push({
      id: c.id,
      name: c.name,
      hp: vitals && vitals.hp !== null && vitals.maxHp !== null ? `HP ${vitals.hp}/${vitals.maxHp}` : null,
      detail: objectNotes.get(`object:${c.id}`) ?? null,
    });
  }

  const zoneLines = zones.flatMap(({ zone, clearance }) => {
    if (!zone.rects.some((r) => cell.x >= r.x && cell.x < r.x + r.w && cell.y >= r.y && cell.y < r.y + r.h)) return [];
    const name = `${ZONE_KIND_LABEL[zone.kind]}${zone.hidden === true ? " · GM ONLY" : ""}`;
    if (!clearance || clearance.total === 0) return [name];
    const here = clearance.obstructedCells.some((o) => o.x === cell.x && o.y === cell.y) ? " · this cell still obstructed" : "";
    return [`${name} · ${clearance.clear}/${clearance.total} CLEAR (${clearancePercent(clearance)}%)${here}`];
  });
  const rooms = Object.values(state.rooms ?? {}).flatMap((room) => (roomContainsCell(room, cell.x, cell.y) ? [room.name] : []));

  const pieces = args.pieces ?? terrainMechanicsPieces(state.terrain, board);
  return {
    key: described.key,
    name: described.tile?.name ?? OPEN_GROUND,
    lines: described.tile?.properties.map((p) => p.line) ?? [],
    units,
    carriers: described.carriers.filter((n) => n.carrier !== "object"),
    zones: [...zoneLines, ...rooms],
    regions: piecesContaining(pieces, described.key).map((p) => p.label),
  };
}
